// Browser notification + audio for agent takeover responses

let lastChimeTime = 0;
const CHIME_COOLDOWN_MS = 3000;

/** Reset the audio cooldown so the next playSound call fires immediately. */
export function resetAudioCooldown() {
	lastChimeTime = 0;
}

export type NotificationSound = 'drop' | 'chime' | 'ping' | 'none';

export const NOTIFICATION_SOUNDS: { value: NotificationSound; label: string }[] = [
	{ value: 'drop', label: 'Drop' },
	{ value: 'chime', label: 'Chime' },
	{ value: 'ping', label: 'Ping' },
	{ value: 'none', label: 'None' },
];

/** Request browser notification permission. Returns true if granted. */
export async function requestTakeoverNotificationPermission(): Promise<boolean> {
	if (typeof window === 'undefined' || !('Notification' in window)) return false;
	if (Notification.permission === 'granted') return true;
	if (Notification.permission === 'denied') return false;
	const result = await Notification.requestPermission();
	return result === 'granted';
}

/** Play a sound by name. Respects 3s cooldown. */
export function playSound(sound: NotificationSound) {
	if (sound === 'none') return;

	const now = Date.now();
	if (now - lastChimeTime < CHIME_COOLDOWN_MS) return;
	lastChimeTime = now;

	try {
		const ctx = new AudioContext();
		const osc = ctx.createOscillator();
		const gain = ctx.createGain();
		osc.connect(gain);
		gain.connect(ctx.destination);

		switch (sound) {
			case 'drop':
				osc.type = 'sine';
				osc.frequency.setValueAtTime(660, ctx.currentTime);
				osc.frequency.exponentialRampToValueAtTime(260, ctx.currentTime + 0.15);
				gain.gain.setValueAtTime(0.2, ctx.currentTime);
				gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
				osc.start(ctx.currentTime);
				osc.stop(ctx.currentTime + 0.2);
				break;
			case 'chime':
				osc.type = 'sine';
				osc.frequency.setValueAtTime(880, ctx.currentTime);
				osc.frequency.setValueAtTime(1108, ctx.currentTime + 0.1);
				gain.gain.setValueAtTime(0.25, ctx.currentTime);
				gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
				osc.start(ctx.currentTime);
				osc.stop(ctx.currentTime + 0.3);
				break;
			case 'ping':
				osc.type = 'triangle';
				osc.frequency.setValueAtTime(1200, ctx.currentTime);
				osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.08);
				gain.gain.setValueAtTime(0.25, ctx.currentTime);
				gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
				osc.start(ctx.currentTime);
				osc.stop(ctx.currentTime + 0.15);
				break;
		}

		osc.onended = () => ctx.close();
	} catch {
		// AudioContext not available
	}
}

export interface NotificationPrefs {
	sound: NotificationSound;
	browserNotifications: boolean;
}

/** Show a browser notification and play sound for a takeover user response. */
export function showTakeoverResponseAlert(
	userId: number,
	userLabel: string,
	messagePreview?: string,
	onClickNavigate?: () => void,
	prefs?: NotificationPrefs,
) {
	const sound = prefs?.sound ?? 'drop';
	const showBrowserNotification = prefs?.browserNotifications ?? true;

	playSound(sound);

	if (!showBrowserNotification) return;
	if (typeof window === 'undefined' || !('Notification' in window)) return;

	// If permission is still "default", request it now (lazy fallback)
	if (Notification.permission === 'default') {
		requestTakeoverNotificationPermission();
		return;
	}

	if (Notification.permission !== 'granted') return;

	const body = messagePreview
		? `"${messagePreview.length > 100 ? messagePreview.substring(0, 100) + '...' : messagePreview}"`
		: 'New message received';

	const n = new Notification(`Takeover: ${userLabel}`, {
		body,
		tag: `takeover-${userId}`,
		requireInteraction: true,
	});
	n.onclick = () => {
		window.focus();
		onClickNavigate?.();
	};
}
